/**
 * Карточка набора: управление составом без ограничений Discord.
 *
 * Именно ради этого экрана из Discord убирается мод-панель: там меню
 * вмещает 25 вариантов, а здесь список любой длины, с поиском и
 * перемещением в один клик.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  EVENT_RESULT_LABEL,
  findMap,
  hasCapability,
  type EventMemberWithProfile,
  type EventResult,
  type EventStatus,
  type MemberList,
  type Profile,
  type RosterEvent,
  type SessionUser,
} from '@ak/shared';

import { apiDelete, apiPatch, apiPost } from '../lib/api.ts';
import { formatCount, formatDateTime, formatRelative } from '../lib/format.ts';
import { navigate } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import { useToast } from '../components/toast.tsx';
import { TimePicker } from '../components/time-picker.tsx';
import { IconGrip } from '../components/icons.tsx';
import { TerritoryPicker } from '../components/territory.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Panel,
  Skeleton,
  TextInput,
  TierBadge,
} from '../components/ui.tsx';

interface EventResponse {
  event: RosterEvent;
  members: EventMemberWithProfile[];
  rollbacks: { userId: string; gameNick: string; submittedAt: string | null; submittedUrl: string | null }[];
}

const STATUS_LABEL: Record<EventStatus, string> = {
  open: 'Открыт',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

const STATUS_TONE: Record<EventStatus, 'positive' | 'neutral' | 'danger'> = {
  open: 'positive',
  closed: 'neutral',
  cancelled: 'danger',
};

/** Кого сейчас тащат мышью и откуда. */
interface DragState {
  userId: string;
  from: MemberList;
}

function MemberRow({
  member,
  canManage,
  busy,
  dragged,
  onMove,
  onRemove,
  onDragStart,
  onDragEnd,
}: {
  member: EventMemberWithProfile;
  canManage: boolean;
  busy: boolean;
  dragged: boolean;
  onMove: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}): ReactNode {
  const draggable = canManage && !busy;

  return (
    <li
      className={`flex items-center justify-between gap-3 px-4 py-2.5 transition-colors ${
        draggable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${dragged ? 'opacity-40' : 'hover:bg-[var(--surface-hover)]'}`}
      style={{ borderTop: '1px solid var(--border-subtle)' }}
      draggable={draggable}
      onDragStart={(event) => {
        // Данные кладём и в dataTransfer: без них перетаскивание
        // не начинается в части браузеров.
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', member.userId);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      <div className="flex min-w-0 items-center gap-3">
        {draggable && (
          <span className="shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true">
            <IconGrip size={16} />
          </span>
        )}
        <TierBadge tier={member.tier} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{member.gameNick}</div>
          <div className="truncate text-xs" style={{ color: 'var(--text-muted)' }}>
            {member.staticId} · {member.userId}
          </div>
        </div>
      </div>

      {canManage && (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button size="sm" onClick={onMove} disabled={busy}>
            {member.list === 'plus' ? 'В замену' : 'В основу'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} disabled={busy} aria-label="Убрать из набора">
            ✕
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Колонка состава: список игроков и приёмник для перетаскивания.
 *
 * Обе колонки устроены одинаково, поэтому описаны одним компонентом:
 * раньше они были копиями друг друга и расходились при правках.
 */
function RosterColumn({
  title,
  counter,
  members,
  list,
  canManage,
  busyUser,
  dragging,
  emptyTitle,
  emptyDescription,
  actions,
  onMove,
  onRemove,
  onDrop,
  onDragStateChange,
}: {
  title: string;
  counter: string;
  members: EventMemberWithProfile[];
  list: MemberList;
  canManage: boolean;
  busyUser: string | null;
  dragging: DragState | null;
  emptyTitle: string;
  emptyDescription: string;
  actions?: ReactNode | undefined;
  onMove: (member: EventMemberWithProfile) => void;
  onRemove: (member: EventMemberWithProfile) => void;
  onDrop: (userId: string, to: MemberList) => void;
  onDragStateChange: (state: DragState | null) => void;
}): ReactNode {
  const [over, setOver] = useState(false);
  // Принимаем только чужих: бросок в свою же колонку ничего не меняет.
  const accepts = dragging !== null && dragging.from !== list;

  return (
    <Panel
      className="overflow-hidden transition-colors"
      style={
        accepts
          ? { borderColor: over ? 'var(--accent)' : 'var(--border-strong)', borderStyle: 'dashed' }
          : undefined
      }
      onDragOver={(event) => {
        if (!accepts) {
          return;
        }

        // Без отмены события браузер не считает область приёмником.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={(event) => {
        // Переход на вложенный элемент — не уход из колонки.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOver(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);

        if (dragging !== null && dragging.from !== list) {
          onDrop(dragging.userId, list);
        }

        onDragStateChange(null);
      }}
    >
      <div className="panel-header flex items-center justify-between gap-3 px-4 py-3">
        <h2 className="font-semibold">
          {title}{' '}
          <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>
            {counter}
          </span>
        </h2>
        {actions}
      </div>

      {members.length === 0 ? (
        <EmptyState
          title={accepts ? 'Отпустите здесь' : emptyTitle}
          description={accepts ? 'Игрок перейдёт в этот список.' : emptyDescription}
        />
      ) : (
        <ul>
          {members.map((member) => (
            <MemberRow
              key={member.userId}
              member={member}
              canManage={canManage}
              busy={busyUser === member.userId}
              dragged={dragging?.userId === member.userId}
              onMove={() => {
                onMove(member);
              }}
              onRemove={() => {
                onRemove(member);
              }}
              onDragStart={() => {
                onDragStateChange({ userId: member.userId, from: list });
              }}
              onDragEnd={() => {
                onDragStateChange(null);
              }}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function AddMemberModal({
  eventId,
  onClose,
  onAdded,
}: {
  eventId: number;
  onClose: () => void;
  onAdded: () => void;
}): ReactNode {
  const toast = useToast();
  const { data, loading } = useResource<{ candidates: Profile[] }>(`/api/events/${String(eventId)}/candidates`);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.candidates ?? [])
      .filter(
        (profile) =>
          needle === '' ||
          profile.gameNick.toLowerCase().includes(needle) ||
          profile.staticId.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [data, query]);

  const add = (userId: string, list: 'plus' | 'reserve'): void => {
    setBusyId(userId);
    apiPost(`/api/events/${String(eventId)}/members`, { userId, list })
      .then(() => {
        toast.success('Игрок добавлен');
        onAdded();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось добавить игрока');
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  return (
    <Modal title="Добавить игрока" onClose={onClose}>
      <Field label="Поиск">
        <TextInput
          value={query}
          placeholder="Ник или статик"
          autoFocus
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </Field>

      <div className="mt-4 max-h-80 overflow-y-auto">
        {loading ? (
          <Skeleton rows={4} />
        ) : candidates.length === 0 ? (
          <EmptyState title="Никого не найдено" description="Добавить можно только игроков с заполненной анкетой." />
        ) : (
          <ul>
            {candidates.map((profile) => (
              <li
                key={profile.userId}
                className="flex items-center justify-between gap-3 py-2.5"
                style={{ borderTop: '1px solid var(--border-subtle)' }}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{profile.gameNick}</div>
                  <div className="truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                    {profile.staticId}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button
                    size="sm"
                    onClick={() => {
                      add(profile.userId, 'reserve');
                    }}
                    disabled={busyId === profile.userId}
                  >
                    В замену
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      add(profile.userId, 'plus');
                    }}
                    disabled={busyId === profile.userId}
                  >
                    В основу
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/**
 * Подтверждение удаления.
 *
 * Отдельное окно со списком последствий: удаление необратимо и уносит
 * состав вместе с сообщениями в Discord, поэтому одной кнопки мало.
 */
function DeleteEventModal({
  event,
  memberCount,
  onClose,
  onDeleted,
}: {
  event: RosterEvent;
  memberCount: number;
  onClose: () => void;
  onDeleted: () => void;
}): ReactNode {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    apiDelete<{ discordCleaned: boolean }>(`/api/events/${String(event.id)}`)
      .then((result) => {
        if (result.discordCleaned) {
          toast.success('Набор удалён');
        } else {
          toast.info('Набор удалён, но сообщение в Discord убрать не вышло — удалите его вручную');
        }

        onDeleted();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось удалить набор');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Удалить набор"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" onClick={submit} busy={busy}>
            Удалить насовсем
          </Button>
        </>
      }
    >
      <p className="text-sm">
        Набор <strong>{event.eventType}</strong> #{event.id} будет удалён из базы.
      </p>
      <ul className="mt-3 space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
        <li>· состав из {formatCount(memberCount, 'человека', 'человек', 'человек')} пропадёт</li>
        <li>· сообщения набора и сводка в Discord будут удалены</li>
        <li>· посещения перестанут учитываться в статистике игроков</li>
        <li>· записи в журнале действий останутся</li>
      </ul>
      <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Если нужно просто прекратить набор, лучше отменить его — тогда история сохранится.
      </p>
    </Modal>
  );
}

function ChangeTimeModal({
  eventId,
  timezone,
  onClose,
  onChanged,
}: {
  eventId: number;
  timezone: string;
  onClose: () => void;
  onChanged: () => void;
}): ReactNode {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    apiPost<{ dm: { sent: number; targeted: number } }>(`/api/events/${String(eventId)}/time`, { startsAt: value })
      .then((result) => {
        toast.success(`Время изменено, уведомлено ${String(result.dm.sent)} из ${String(result.dm.targeted)}`);
        onChanged();
        onClose();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось изменить время');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Сменить время"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={submit} busy={busy}>
            Сменить и уведомить
          </Button>
        </>
      }
    >
      <Field label="Новое время" hint={`Часовой пояс сообщества: ${timezone}`}>
        <TimePicker value={value} timezone={timezone} onChange={setValue} />
      </Field>
      <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Всем участникам набора уйдёт личное сообщение о переносе.
      </p>
    </Modal>
  );
}

export function EventDetailPage({
  eventId,
  user,
  timezone,
}: {
  eventId: number;
  user: SessionUser;
  timezone: string;
}): ReactNode {
  const toast = useToast();
  const { data, loading, error, reload } = useResource<EventResponse>(
    `/api/events/${String(eventId)}`,
    (event) =>
      (event.type === 'event.changed' && event.eventId === eventId) || event.type === 'events.changed',
  );

  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [resultBusy, setResultBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [changingTime, setChangingTime] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);

  const canManageMembers = hasCapability(user.level, 'members.manage');
  const canManageEvent = hasCapability(user.level, 'events.manage');
  const canCancel = hasCapability(user.level, 'events.cancel');
  // Удаление отделено от отмены: отменённый набор можно открыть заново,
  // удалённый не вернуть.
  const canDelete = hasCapability(user.level, 'events.delete');

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading || data === null) {
    return (
      <Panel>
        <Skeleton rows={8} />
      </Panel>
    );
  }

  const { event, members } = data;
  const plus = members.filter((member) => member.list === 'plus');
  const reserve = members.filter((member) => member.list === 'reserve');

  const moveTo = (userId: string, target: MemberList): void => {
    setBusyUser(userId);

    apiPatch(`/api/events/${String(eventId)}/members/${userId}`, { list: target })
      .then(() => {
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось перенести игрока');
      })
      .finally(() => {
        setBusyUser(null);
      });
  };

  const move = (member: EventMemberWithProfile): void => {
    moveTo(member.userId, member.list === 'plus' ? 'reserve' : 'plus');
  };

  const remove = (member: EventMemberWithProfile): void => {
    setBusyUser(member.userId);
    apiDelete(`/api/events/${String(eventId)}/members/${member.userId}`)
      .then(() => {
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось убрать игрока');
      })
      .finally(() => {
        setBusyUser(null);
      });
  };

  const setStatus = (status: EventStatus): void => {
    setStatusBusy(true);
    apiPost(`/api/events/${String(eventId)}/status`, { status })
      .then(() => {
        toast.success(status === 'open' ? 'Набор открыт' : status === 'closed' ? 'Набор закрыт' : 'Набор отменён');
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось изменить состояние');
      })
      .finally(() => {
        setStatusBusy(false);
      });
  };

  const setResult = (result: EventResult | null): void => {
    setResultBusy(true);
    apiPost<{ cleaned?: number }>(`/api/events/${String(eventId)}/result`, { result })
      .then((response) => {
        const cleaned = response.cleaned ?? 0;
        toast.success(
          result === null
            ? 'Итог снят'
            : `Отмечено: ${EVENT_RESULT_LABEL[result].toLowerCase()}` +
                (cleaned > 0 ? ` · из канала плюсов удалено сообщений: ${String(cleaned)}` : ''),
        );
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отметить итог');
      })
      .finally(() => {
        setResultBusy(false);
      });
  };

  const notify = (): void => {
    apiPost<{ dm: { sent: number; targeted: number } }>(`/api/events/${String(eventId)}/notify`)
      .then((result) => {
        toast.success(`Уведомлено ${String(result.dm.sent)} из ${String(result.dm.targeted)}`);
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отправить уведомления');
      });
  };

  /*
   * Ручная проверка сбора: автоматическая идёт перед началом, а эта нужна,
   * когда время сместилось или собрались раньше.
   */
  const checkVoice = (): void => {
    setVoiceBusy(true);
    apiPost<{ checked: number; missing: number; moved: number }>(`/api/events/${String(eventId)}/voice-check`)
      .then((result) => {
        toast.success(
          result.checked === 0
            ? 'В основном составе никого — проверять нечего'
            : `Позвано ${String(result.missing)}, перенесено ${String(result.moved)} из ${String(result.checked)}`,
        );
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось проверить сбор');
      })
      .finally(() => {
        setVoiceBusy(false);
      });
  };

  return (
    <>
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-3 -ml-2"
          onClick={() => {
            navigate('/events');
          }}
        >
          ← К списку наборов
        </Button>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{event.eventType}</h1>
              <Badge tone={STATUS_TONE[event.status]}>{STATUS_LABEL[event.status]}</Badge>
              {event.result !== null && (
                <Badge tone={event.result === 'win' ? 'positive' : event.result === 'loss' ? 'danger' : 'neutral'} dot>
                  {EVENT_RESULT_LABEL[event.result]}
                </Badge>
              )}
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                #{event.id}
              </span>
            </div>
            <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {formatDateTime(event.startsAt, timezone)}
              <span style={{ color: 'var(--text-muted)' }}> · {formatRelative(event.startsAt)}</span>
              {event.territory !== null && <span style={{ color: 'var(--text-muted)' }}> · тера {event.territory}</span>}
              {findMap(event.eventType, event.mapId) !== null && (
                <span style={{ color: 'var(--text-muted)' }}>
                  {' '}
                  · карта {findMap(event.eventType, event.mapId)?.label}
                </span>
              )}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {canManageEvent && (
              <>
                <Button
                  onClick={() => {
                    setChangingTime(true);
                  }}
                >
                  Сменить время
                </Button>
                <Button onClick={notify}>Уведомить</Button>
                <Button onClick={checkVoice} busy={voiceBusy}>
                  Проверить сбор
                </Button>
                <Button
                  onClick={() => {
                    setStatus(event.status === 'open' ? 'closed' : 'open');
                  }}
                  busy={statusBusy}
                  disabled={event.status === 'cancelled'}
                >
                  {event.status === 'open' ? 'Закрыть набор' : 'Открыть набор'}
                </Button>
              </>
            )}
            {canCancel && event.status !== 'cancelled' && (
              <Button
                variant="danger"
                onClick={() => {
                  setStatus('cancelled');
                }}
                busy={statusBusy}
              >
                Отменить
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                aria-label="Удалить набор"
                onClick={() => {
                  setDeleting(true);
                }}
              >
                Удалить
              </Button>
            )}
          </div>
        </div>

        {event.description !== null && event.description !== '' && (
          <p className="mt-4 max-w-3xl text-sm" style={{ color: 'var(--text-secondary)' }}>
            {event.description}
          </p>
        )}

        {/* Итог отмечается у сыгранного набора: у идущего его ещё нет,
            у отменённого мероприятия не будет. */}
        {canManageEvent && event.status === 'closed' && (
          <Panel className="mt-4 flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="text-sm font-medium">
              {event.result === null ? 'Чем закончилось мероприятие?' : 'Итог мероприятия'}
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={event.result === 'win' ? 'primary' : 'secondary'}
                busy={resultBusy}
                onClick={() => {
                  setResult('win');
                }}
              >
                🏆 Победа
              </Button>
              <Button
                size="sm"
                variant={event.result === 'loss' ? 'danger' : 'secondary'}
                busy={resultBusy}
                onClick={() => {
                  setResult('loss');
                }}
              >
                💀 Поражение
              </Button>
              {/* АФК — нейтральный итог: выбранный горит серым, а не цветом победы или поражения. */}
              <Button
                size="sm"
                variant="secondary"
                busy={resultBusy}
                {...(event.result === 'afk'
                  ? {
                      style: {
                        background: 'var(--text-muted)',
                        color: 'var(--surface-page)',
                        border: '1px solid var(--text-muted)',
                      },
                    }
                  : {})}
                onClick={() => {
                  setResult('afk');
                }}
              >
                💤 АФК
              </Button>
              {event.result !== null && (
                <Button
                  size="sm"
                  variant="ghost"
                  busy={resultBusy}
                  onClick={() => {
                    setResult(null);
                  }}
                >
                  Снять отметку
                </Button>
              )}
            </div>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Итог виден в Discord — в карточке набора и в сводке для модераторов.
            </span>
          </Panel>
        )}

        {/* Тера отмечается рядом с итогом: по ним вместе считается винрейт по терам. */}
        {canManageEvent && event.status !== 'cancelled' && (
          <TerritoryPicker eventId={eventId} territory={event.territory} onChanged={reload} />
        )}
      </div>

      {canManageMembers && (
        <p className="mb-3 text-sm" style={{ color: 'var(--text-muted)' }}>
          Игрока можно перетащить мышью из одного списка в другой — или воспользоваться кнопками в строке.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <RosterColumn
          title="Замена"
          counter={String(reserve.length)}
          members={reserve}
          list="reserve"
          canManage={canManageMembers}
          busyUser={busyUser}
          dragging={dragging}
          emptyTitle="В замене никого"
          emptyDescription="Игроки попадают сюда после нажатия кнопки в Discord."
          onMove={move}
          onRemove={remove}
          onDrop={moveTo}
          onDragStateChange={setDragging}
        />

        <RosterColumn
          title="Основной состав"
          counter={`${String(plus.length)}/${String(event.capacity)}`}
          members={plus}
          list="plus"
          canManage={canManageMembers}
          busyUser={busyUser}
          dragging={dragging}
          emptyTitle="Основной состав пуст"
          emptyDescription="Переносите игроков из замены."
          actions={
            canManageMembers ? (
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setAdding(true);
                }}
              >
                Добавить
              </Button>
            ) : undefined
          }
          onMove={move}
          onRemove={remove}
          onDrop={moveTo}
          onDragStateChange={setDragging}
        />
      </div>

      {data.rollbacks.length > 0 && (
        <Panel className="mt-4 overflow-hidden">
          <div className="px-4 py-3" style={{ background: 'var(--surface-raised)' }}>
            <h2 className="font-semibold">
              Откаты{' '}
              <span style={{ color: 'var(--text-muted)' }}>
                {formatCount(data.rollbacks.filter((row) => row.submittedAt !== null).length, 'сдан', 'сдано', 'сдано')} из{' '}
                {data.rollbacks.length}
              </span>
            </h2>
          </div>
          <ul>
            {data.rollbacks.map((row) => (
              <li
                key={row.userId}
                className="flex items-center justify-between gap-3 px-4 py-2.5"
                style={{ borderTop: '1px solid var(--border-subtle)' }}
              >
                <span className="text-sm font-medium">{row.gameNick}</span>
                {row.submittedAt === null ? (
                  <Badge tone="warning">не сдан</Badge>
                ) : row.submittedUrl === null ? (
                  <Badge tone="positive">сдан</Badge>
                ) : (
                  <a
                    href={row.submittedUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm font-medium underline"
                    style={{ color: 'var(--accent)' }}
                  >
                    ссылка
                  </a>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {adding && (
        <AddMemberModal
          eventId={eventId}
          onClose={() => {
            setAdding(false);
          }}
          onAdded={reload}
        />
      )}

      {deleting && (
        <DeleteEventModal
          event={event}
          memberCount={members.length}
          onClose={() => {
            setDeleting(false);
          }}
          onDeleted={() => {
            navigate('/events');
          }}
        />
      )}

      {changingTime && (
        <ChangeTimeModal
          eventId={eventId}
          timezone={timezone}
          onClose={() => {
            setChangingTime(false);
          }}
          onChanged={reload}
        />
      )}
    </>
  );
}
